import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import formService from '../services/formService';
import pdfService from '../services/pdfService';
import { PhotoFieldPreview } from '../components/PhotoFieldUpload';
import { DynamicRowsPreview } from '../components/DynamicRowsField';

const ViewSubmissionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [submission, setSubmission] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sr Inspector sticky notes
  const NOTES_KEY = `sr_notes_${id}`;
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesText, setNotesText] = useState('');

  useEffect(() => {
    fetchSubmissionDetails();
    fetchPDFList();
    // Load any previously saved notes for this submission
    const saved = localStorage.getItem(`sr_notes_${id}`);
    if (saved) {
      setNotesText(saved);
      setNotesOpen(true);
    }
  }, [id]);

  const fetchSubmissionDetails = async () => {
    try {
      setLoading(true);
      const data = await formService.getSubmissionDetails(id);
      setSubmission(data.submission);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load submission details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPDFList = async () => {
    try {
      const data = await pdfService.listCertificates(id);
      setPdfs(data.pdfs || []);
    } catch (err) {
      // PDF list is optional, don't show error
      console.error('Failed to fetch PDFs:', err);
    }
  };

  const handleDownloadPDF = async (pdfId, filename) => {
    try {
      const blob = await pdfService.downloadCertificate(pdfId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download certificate: ' + (err.message || 'Unknown error'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // --- Sr Inspector Notes handlers ---
  const handleNotesChange = (val) => {
    setNotesText(val);
    localStorage.setItem(NOTES_KEY, val);
  };

  const handleClearNotes = () => {
    setNotesText('');
    localStorage.removeItem(NOTES_KEY);
  };

  const handleSaveAndReview = () => {
    localStorage.setItem(NOTES_KEY, notesText);
    navigate('/dashboard/sr_inspector');
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'submitted':
        return 'bg-primary';
      case 'reviewed_by_senior':
        return 'bg-info';
      case 'sent_back_to_junior':
        return 'bg-warning';
      case 'approved_by_admin':
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  };

  const STATUS_LABELS = {
    submitted_by_junior: 'Submitted By Inspector',
    sent_back_by_senior: 'Sent Back By Reviewer',
    reviewed_by_senior: 'Reviewed By Reviewer',
    approved_by_admin: 'Approved By Admin'
  };

  const formatStatus = (status) => {
    if (!status) return 'N/A';
    return STATUS_LABELS[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const ROLE_LABELS = {
    jr_inspector: 'Inspector',
    sr_inspector: 'Reviewer',
    admin: 'Admin'
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group form data by sections
  const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const groupDataBySection = () => {
    if (!submission || !submission.form_data) return {};

    const grouped = {};

    // Convert form_data object to array of values
    const formDataArray = Object.values(submission.form_data);

    formDataArray.forEach(data => {
      const section = data.field.field_section || 'Other Information';
      if (!grouped[section]) {
        grouped[section] = [];
      }
      grouped[section].push({
        field_label: data.field.field_label,
        field_value: data.value,
        field_order: data.field.field_order,
        field_type: data.field.field_type  // Add field_type for proper checkbox handling
      });
    });

    // Sort fields within each section by field_order
    Object.keys(grouped).forEach(section => {
      grouped[section].sort((a, b) => a.field_order - b.field_order);
    });

    return grouped;
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading submission details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning" role="alert">
          Submission not found
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const groupedData = groupDataBySection();

  return (
    <div className="container mt-4 mb-5">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 no-print">
        <div>
          <h2>Submission Details</h2>
          <p className="text-muted mb-0">Form Number: <strong>{submission.form_number}</strong></p>
        </div>
        <div>
          <button className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-2"></i>Back
          </button>
          <button className="btn btn-outline-primary me-2" onClick={handlePrint}>
            <i className="fas fa-print me-2"></i>Print
          </button>
          {pdfs.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={() => handleDownloadPDF(pdfs[0].pdf_id, pdfs[0].pdf_filename)}
            >
              <i className="fas fa-download me-2"></i>Download Certificate
            </button>
          )}
        </div>
      </div>

      {/* Basic Information Card */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="fas fa-info-circle me-2"></i>Basic Information
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="text-muted small">Form Number</label>
              <p className="fw-bold mb-0">{submission.form_number}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted small">Status</label>
              <p className="mb-0">
                <span className={`badge ${getStatusBadgeClass(submission.status)}`}>
                  {formatStatus(submission.status)}
                </span>
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted small">Client</label>
              <p className="fw-bold mb-0">{submission.client_name || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted small">Equipment Type</label>
              <p className="fw-bold mb-0">{submission.equipment_type_name || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted small">Form Type</label>
              <p className="fw-bold mb-0">{submission.form_type_name || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted small">Inspection Date</label>
              <p className="fw-bold mb-0">
                {submission.inspection_date ? new Date(submission.inspection_date).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted small">Submitted By</label>
              <p className="fw-bold mb-0">
                {submission.submitter_name || 'N/A'}
                <span className="text-muted small"> ({ROLE_LABELS[submission.submitter_role] || submission.submitter_role})</span>
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted small">Submitted On</label>
              <p className="fw-bold mb-0">{formatDate(submission.submitted_at)}</p>
            </div>
            {submission.reviewed_by_name && (
              <>
                <div className="col-md-6 mb-3">
                  <label className="text-muted small">Reviewed By</label>
                  <p className="fw-bold mb-0">{submission.reviewed_by_name}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted small">Reviewed On</label>
                  <p className="fw-bold mb-0">{formatDate(submission.reviewed_at)}</p>
                </div>
              </>
            )}
            {submission.approved_by_name && (
              <>
                <div className="col-md-6 mb-3">
                  <label className="text-muted small">Approved By</label>
                  <p className="fw-bold mb-0">{submission.approved_by_name}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted small">Approved On</label>
                  <p className="fw-bold mb-0">{formatDate(submission.approved_at)}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Form Data Sections */}
      {Object.keys(groupedData).map((section, index) => (
        <div key={index} className="card mb-4 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="mb-0">
              <i className="fas fa-list-ul me-2"></i>{section}
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-borderless">
                <tbody>
                  {groupedData[section]
                    .sort((a, b) => a.field_order - b.field_order)
                    .map((field, idx) => {
                      // Format checkbox values
                      let displayValue = field.field_value;

                      // Handle boolean/checkbox values for checkbox type fields
                      if (field.field_type === 'checkbox') {
                        if (displayValue === 'true' || displayValue === true) {
                          displayValue = '☑ Selected';
                        } else if (displayValue === 'false' || displayValue === false || !displayValue || displayValue === '' || displayValue === 'null') {
                          displayValue = '☐ Not selected';
                        }
                      } else if (field.field_type === 'file') {
                        displayValue = (
                          <PhotoFieldPreview filename={displayValue} alt={field.field_label} size={100} />
                        );
                      } else if (field.field_type === 'table_rows') {
                        displayValue = <DynamicRowsPreview value={displayValue} />;
                      } else {
                        // For non-checkbox fields, show "Not provided" if empty
                        if (!displayValue || displayValue === '' || displayValue === 'null') {
                          displayValue = <span className="text-muted fst-italic">Not provided</span>;
                        }
                      }

                      return (
                        <tr key={idx}>
                          <td width="40%" className="text-muted">{field.field_label}</td>
                          <td width="60%" className="fw-bold">
                            {displayValue}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}

      {/* Status History Timeline */}
      {submission.status_history && submission.status_history.length > 0 && (() => {
        // Count total sent-back events (history is newest-first, so reverse to number chronologically)
        const sentBackTotal = submission.status_history.filter(h => h.status === 'sent_back_by_senior').length;
        // We'll number sent-back entries chronologically (oldest = 1st).
        // History is newest-first, so track a descending counter.
        let sentBackCounter = sentBackTotal;

        return (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-history me-2"></i>Status History
              </h5>
              {sentBackTotal > 0 && (
                <span className="badge bg-danger fs-6">
                  <i className="fas fa-undo me-1"></i>
                  Sent back {sentBackTotal} {sentBackTotal === 1 ? 'time' : 'times'}
                </span>
              )}
            </div>
            <div className="card-body">
              <div className="timeline">
                {submission.status_history.map((history, index) => {
                  let labelText;
                  let markerClass = getStatusBadgeClass(history.status);
                  if (history.status === 'sent_back_by_senior') {
                    const num = sentBackCounter--;
                    labelText = `${ordinal(num)} Correction Requested`;
                    markerClass = 'bg-danger';
                  } else {
                    labelText = formatStatus(history.status);
                  }

                  return (
                    <div key={index} className="timeline-item mb-4">
                      <div className="d-flex">
                        <div className="timeline-marker me-3">
                          <div className={`badge ${markerClass} rounded-circle`}
                               style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className={`fas ${history.status === 'sent_back_by_senior' ? 'fa-undo' : 'fa-check'}`}></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-0">{labelText}</h6>
                            <small className="text-muted">{formatDate(history.changed_at)}</small>
                          </div>
                          {history.changed_by_name && (
                            <p className="text-muted small mb-1">
                              By: <strong>{history.changed_by_name}</strong>
                            </p>
                          )}
                          {history.comments && (
                            <div className={`alert ${history.status === 'sent_back_by_senior' ? 'alert-danger' : 'alert-light'} border mb-0 mt-2`}>
                              <strong>Comments:</strong> {history.comments}
                            </div>
                          )}
                        </div>
                      </div>
                      {index < submission.status_history.length - 1 && (
                        <div style={{ marginLeft: '19px', height: '20px', borderLeft: '2px solid #dee2e6' }}></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* PDF Certificates */}
      {pdfs.length > 0 && (
        <div className="card mb-4 shadow-sm">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-file-pdf me-2"></i>Generated Certificates
            </h5>
          </div>
          <div className="card-body">
            <div className="list-group">
              {pdfs.map((pdf, index) => (
                <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">{pdf.pdf_filename}</h6>
                    <small className="text-muted">
                      Generated: {formatDate(pdf.generated_at)}
                      {pdf.sent_at && ` | Sent: ${formatDate(pdf.sent_at)}`}
                    </small>
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleDownloadPDF(pdf.pdf_id, pdf.pdf_filename)}
                  >
                    <i className="fas fa-download me-1"></i>Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sr Inspector Sticky Notes Panel */}
      {user?.role === 'sr_inspector' && (
        <div
          className="no-print"
          style={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Expandable panel */}
          {notesOpen && (
            <div style={{
              width: '300px',
              background: '#fff',
              border: '1px solid #dee2e6',
              borderRight: 'none',
              borderRadius: '8px 0 0 8px',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.18)',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: '460px',
            }}>
              {/* Panel header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.9em' }}>
                  <i className="fas fa-sticky-note me-1" style={{ color: '#ffc107' }}></i>
                  Review Notes
                </strong>
                <span style={{ fontSize: '0.72em', color: '#6c757d' }}>
                  <i className="fas fa-save me-1"></i>Auto-saved
                </span>
              </div>
              <div style={{ fontSize: '0.75em', color: '#6c757d', marginTop: '-4px' }}>
                Jot down corrections as you scroll
              </div>

              {/* Notes textarea */}
              <textarea
                style={{
                  flex: 1,
                  minHeight: '230px',
                  resize: 'vertical',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '8px',
                  fontSize: '0.85em',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                }}
                placeholder={"e.g.\nWire rope SWL: value incorrect\nNext inspection date: missing\nSerial No: not matching tag"}
                value={notesText}
                onChange={e => handleNotesChange(e.target.value)}
              />

              {/* Save & Review button */}
              <button
                style={{
                  background: notesText.trim() ? '#198754' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '9px',
                  cursor: notesText.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '0.85em',
                  fontWeight: 'bold',
                }}
                onClick={handleSaveAndReview}
                disabled={!notesText.trim()}
                title="Save notes and go to dashboard to open Review modal"
              >
                <i className="fas fa-arrow-left me-1"></i>
                Save &amp; Go to Review
              </button>

              {/* Clear button */}
              <button
                style={{
                  background: 'transparent',
                  color: '#dc3545',
                  border: '1px solid #dc3545',
                  borderRadius: '4px',
                  padding: '5px 8px',
                  cursor: 'pointer',
                  fontSize: '0.78em',
                }}
                onClick={handleClearNotes}
              >
                <i className="fas fa-trash me-1"></i>Clear Notes
              </button>
            </div>
          )}

          {/* Side tab toggle */}
          <button
            onClick={() => setNotesOpen(o => !o)}
            title={notesOpen ? 'Close notes panel' : 'Open review notes'}
            style={{
              writingMode: 'vertical-rl',
              background: notesText.trim() ? '#ffc107' : '#0d6efd',
              color: notesText.trim() ? '#000' : '#fff',
              border: 'none',
              padding: '16px 10px',
              cursor: 'pointer',
              borderRadius: '8px 0 0 8px',
              fontSize: '0.78em',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              boxShadow: '-3px 0 10px rgba(0,0,0,0.2)',
              userSelect: 'none',
            }}
          >
            {notesOpen ? '▶ Close' : (notesText.trim() ? '◀ Write a Review ✎' : '◀ Write a Review')}
          </button>
        </div>
      )}

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .card {
            page-break-inside: avoid;
            border: 1px solid #dee2e6 !important;
          }
          body {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ViewSubmissionDetails;
